import { Fragment } from 'react'
import { lookFamily, type SettingLookFamily } from '@gk/contracts'
import type { SettingLine } from '../../data/plan.ts'
import type { LookFrame } from '../../data/lookGeometry.ts'
import { LookPicture } from './LookPicture.tsx'

/** M25-P (docs/M25.md rule 9, E): A JOB'S PICTURES IN A LINE, 1 em high, from `settingLines` (the declared order).
 *  - `pictures` (the card's closed line, the week's chips): the pictures alone, hidden from assistive technology (the words beside them carry the
 *    meaning); nothing at all when no setting has one. `families` keeps only those (the chip: the direction or pattern, and the lap).
 *  - `values` (the daily sheet, rule 9): a setting whose value has a picture prints the picture and the value WITHOUT its label (the label stays for a
 *    screen reader), a setting without one prints as today (« label value unit »), « · » between; a picture never parts from its word.
 *  The colour is the text's (currentColor): the page sets the token and its dark variant. */
export function LookStrip({ lines, frame = 'round', mode = 'pictures', families, className = '', testId }: {
  lines: readonly SettingLine[]; frame?: LookFrame; mode?: 'pictures' | 'values'; families?: readonly SettingLookFamily[]; className?: string; testId?: string
}) {
  const kept = families ? lines.filter((l) => !l.look || families.includes(lookFamily(l.look)!)) : lines
  if (mode === 'pictures') {
    const pictured = kept.filter((l) => l.look)
    if (!pictured.length) return null
    return (
      <span className={`inline-flex items-center gap-[0.3em] align-[-0.15em] ${className}`} aria-hidden="true" data-testid={testId}>
        {pictured.map((l) => <LookPicture key={l.key} code={l.look!} frame={frame} className="size-[1em] shrink-0" />)}
      </span>
    )
  }
  if (!kept.length) return null
  return (
    <span className={className} data-testid={testId}>
      {kept.map((l, i) => (
        <Fragment key={l.key}>
          {i > 0 && ' · '}
          {l.look
            ? <span className="whitespace-nowrap"><LookPicture code={l.look} frame={frame} className="mr-[0.2em] inline-block size-[1em] align-[-0.15em]" /><span className="sr-only">{`${l.label} `}</span>{`${l.value}${l.unit ? ' ' + l.unit : ''}`}</span>
            : `${l.label} ${l.value}${l.unit ? ' ' + l.unit : ''}`}
        </Fragment>
      ))}
    </span>
  )
}
